import { useState } from "react";
import axios from "axios";
import { Plus, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { API, formatApiError, getInitials } from "@/lib/shared";

export const STAFF_ROLES = ["RN", "LPN", "Secretary", "Admin", "Medical Doctor", "Office Manager"];

export const StaffManagement = ({ staff, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'RN', color: '#2D5A4C' });

  const openCreate = () => { setEditingStaff(null); setFormData({ first_name: '', last_name: '', email: '', phone: '', role: 'RN', color: '#2D5A4C' }); setModalOpen(true); };
  const openEdit = (s) => { setEditingStaff(s); setFormData({ first_name: s.first_name, last_name: s.last_name, email: s.email || '', phone: s.phone || '', role: s.role, color: s.color || '#2D5A4C' }); setModalOpen(true); };

  const handleSave = async () => {
    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.id}`, formData);
        toast.success("Staff updated");
      } else {
        await axios.post(`${API}/staff`, formData);
        toast.success("Staff added");
      }
      setModalOpen(false);
      onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try { await axios.delete(`${API}/staff/${id}`); toast.success("Staff deleted"); onRefresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Staff Members</h2>
        <Button size="sm" onClick={openCreate} data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" />Add Staff</Button>
      </div>
      <div className="space-y-2">
        {staff.map(s => (
          <div key={s.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={s.avatar_url} />
                <AvatarFallback style={{ backgroundColor: s.color + '20', color: s.color }}>{getInitials(s.first_name, s.last_name)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{s.first_name} {s.last_name}</p>
                <p className="text-sm text-muted-foreground">{s.role}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingStaff ? "Edit Staff" : "Add Staff"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>First Name</Label><Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} data-testid="staff-firstname" /></div>
              <div className="space-y-2"><Label>Last Name</Label><Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} data-testid="staff-lastname" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger data-testid="staff-role-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_ROLES.map(r => <SelectItem key={r} value={r} data-testid={`staff-role-${r.toLowerCase().replace(/\s+/g, '-')}`}>{r}</SelectItem>)}
                  {formData.role && !STAFF_ROLES.includes(formData.role) && <SelectItem value={formData.role}>{formData.role} (legacy)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Input type="color" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} className="h-10 w-20" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-staff-btn">{editingStaff ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PATIENT MANAGEMENT ====================
